import { type Timer } from "@/shared/components/visualization/timer/Timer";
import { isAbortError } from "@/shared/errors";
import { type DownloadPriority, type Message } from "@/shared/reader";
import { LoggerService } from "@/shared/services/LoggerService";
import { type TopicData } from "@/shared/state/visualization";
import {
  type MessagePayload,
  type OnMessageCallback,
  WorkerManager,
} from "@/shared/webworker";

import {
  type IndexRange,
  RawMessageCommand,
  SOURCE,
} from "./messaging/commands";
import {
  IndexState,
  isErrorEvent,
  isInitializedEvent,
  isTimeIndexStateChangeEvent,
  isLoadingStateChangeEvent,
} from "./messaging/events";
import { PlaybackDirection, RawMessageCache } from "./RawMessageCache";

type Realm = "UIThread" | "WebWorker";
type LoadingStates = { [K in Realm]: DownloadPriority | false };

/** Factory that receives the manager's event handlers and returns a WorkerManager. */
export type WorkerManagerFactory = (init: {
  onMessage: OnMessageCallback;
  onError: (error: Error) => void;
  signal: AbortSignal;
}) => WorkerManager;

export class RawMessageManager {
  private abortController: AbortController;
  private cache: RawMessageCache;
  private lastEmittedIndex: number | null = null;
  private loadingStates: LoadingStates = { UIThread: false, WebWorker: false };
  private onErrorCallback: (error: Error) => void = () => {};
  private onInitializedCallback: () => void = () => {};
  private onLoadingChangeCallback?: (state: DownloadPriority | false) => void;
  private onMessageCallback?: (message: Message | null) => void;
  private prevTopicId: string | null = null;
  private timer: Timer | null = null;
  private workerManager: WorkerManager;

  /**
   * @param createWorkerManager — optional factory for testing. When omitted a
   *   real Web Worker is created (production path). The factory receives the
   *   manager's event handlers so that tests can simulate worker events
   *   without reaching into private state.
   * @param cacheOptions — forwarded to the underlying `RawMessageCache`. Lets
   *   tests pin batch / cache sizes so assertions don't depend on the
   *   `DEFAULT_BATCH_SIZE` constant.
   */
  constructor(
    createWorkerManager?: WorkerManagerFactory,
    cacheOptions?: ConstructorParameters<typeof RawMessageCache>[0],
  ) {
    this.cache = new RawMessageCache(cacheOptions);
    this.abortController = new AbortController();

    const init = {
      onMessage: this.handleMessage,
      onError: (err: Error) => this.onErrorCallback(err),
      signal: this.abortController.signal,
    };

    if (createWorkerManager) {
      this.workerManager = createWorkerManager(init);
    } else {
      const worker = new Worker(
        new URL("./RawMessageService.worker", import.meta.url),
        { type: "module", name: `RawMessageService_${crypto.randomUUID()}` },
      );

      this.workerManager = new WorkerManager({ worker, ...init });
    }

    this.workerManager.sendCommand({
      data: undefined,
      source: SOURCE,
      type: RawMessageCommand.Init,
      signal: this.abortController.signal,
    });
  }

  /**
   * Whether the cache holds a non-empty timestamp index. False both before
   * the worker delivers an index and after an `IndexState.Empty` event
   * clears the cache for a topic with no messages in range — callers that
   * need to distinguish those cases should listen for index events.
   */
  get hasIndex(): boolean {
    return this.cache.length > 0;
  }

  dispose(): void {
    this.abortController.abort();
    this.workerManager.sendCommand({
      data: undefined,
      source: SOURCE,
      type: RawMessageCommand.Dispose,
    });
    this.workerManager.dispose();
  }

  /**
   * Fills any gaps in the cache around the current playback position so the
   * panel can render the message under the playhead. Driven by the panel's
   * `useMessages` poller on a 50 ms cadence; the timer's tick / seek /
   * start events only emit the cached value and never reach this method.
   *
   * Per call:
   *
   * 1. Resolve the index for the timer's current time. If the playhead is
   *    outside the topic's recorded range, emit `null` and return so the
   *    "no recorded messages" placeholder can render.
   * 2. Ask the cache which surrounding indices are missing, biased by the
   *    inferred playback direction.
   * 3. Issue one worker request per missing range, in parallel, and merge
   *    each response into the cache.
   * 4. Emit the cached message at the playhead so the panel updates without
   *    waiting for the next timer tick.
   * 5. On exit, trim the cache around the latest emitted index, biased by
   *    the same playback direction used for the fetch, so the keep-window
   *    covers the just-prefetched lookahead and bounds memory without
   *    evicting the entries this poll cycle just produced.
   *
   * The visible loading indicator only flips on when the user-facing
   * message under the playhead is itself missing; pure prefetch of
   * surrounding indices stays silent. The indicator is always cleared in
   * `finally` so that the "no recorded messages" placeholder — which only
   * renders when nothing is loading — can appear after an abort or error
   * instead of being masked by a stuck spinner.
   *
   * Failure-mode posture:
   *
   * - An `AbortError` from a worker request means the poll cycle's signal
   *   was cancelled (effect cleanup or dependency change); it is swallowed
   *   so the next poll picks up the new state cleanly.
   * - Any other exception propagates to `useMessages`, which reports it via
   *   `ErrorMonitoringService` and continues polling.
   */
  async loadMessages(signal: AbortSignal): Promise<void> {
    if (
      signal.aborted ||
      this.abortController.signal.aborted ||
      this.timer === null ||
      !this.hasIndex
    ) {
      return;
    }

    const time = this.timer.currentTime;
    const targetIndex = this.cache.timestampToIndex(time);

    if (targetIndex === -1) {
      // Playhead is outside the topic's recorded range. Clearing the
      // UIThread loading flag here is load-bearing: a concurrent or
      // prior fetch may have left the indicator on "foreground", and
      // without releasing it the panel would keep showing a spinner
      // instead of the "no recorded messages" placeholder.
      this.onMessageCallback?.(null);
      this.loadingStateChange("UIThread", false);
      return;
    }

    const direction =
      this.lastEmittedIndex !== null && targetIndex < this.lastEmittedIndex
        ? PlaybackDirection.Backward
        : PlaybackDirection.Forward;
    this.lastEmittedIndex = targetIndex;

    const playbackRate = this.timer.playbackRate;
    const { missing, isTargetMissing } = this.cache.getIndicesForMissingData(
      targetIndex,
      direction,
      playbackRate,
    );

    if (missing.length === 0) {
      this.emitMessageAtCurrentTime();
      return;
    }

    if (isTargetMissing) {
      this.loadingStateChange("UIThread", "foreground");
    }

    try {
      // Fan out one worker request per missing range. Each call gets a
      // correlation ID so concurrent fetches — and any unrelated worker
      // events arriving through `handleMessage` — don't cross-talk.
      const batches = await Promise.all(
        missing.map((range) =>
          this.workerManager.sendCommandAwaitResponse<
            RawMessageCommand,
            IndexRange,
            Message[]
          >({
            data: range,
            source: SOURCE,
            type: RawMessageCommand.GetMessages,
            signal,
          }),
        ),
      );

      for (const messages of batches) {
        this.cache.addMessages(messages);
      }

      this.emitMessageAtCurrentTime();
    } catch (err) {
      if (isAbortError(err)) {
        return;
      }
      throw err;
    } finally {
      // Always release the loading indicator and trim the cache, even on
      // abort or error. Leaving the indicator on would mask the
      // "no recorded messages" placeholder; skipping the trim would let
      // the cache grow without bound across long playback sessions.
      //
      // `direction` is passed through so the trim's keep-window matches
      // the bias used by `getIndicesForMissingData` / `bucketize` above.
      // The cache's keep-window is asymmetric — wider in the direction of
      // travel — and must cover the just-fetched lookahead range, or the
      // trim could evict entries the same poll cycle produced.
      this.loadingStateChange("UIThread", false);
      if (this.lastEmittedIndex !== null) {
        this.cache.trimCache(this.lastEmittedIndex, direction);
      }
    }
  }

  setOnError(callback: (error: Error) => void): void {
    this.onErrorCallback = callback;
  }

  setOnInitialized(callback: () => void): void {
    this.onInitializedCallback = callback;
  }

  setOnLoadingChange(
    callback: (state: DownloadPriority | false) => void,
  ): void {
    this.onLoadingChangeCallback = callback;
  }

  setOnMessage(callback: (message: Message | null) => void): void {
    this.onMessageCallback = callback;
  }

  /**
   * Pushes the panel's selected topic and message paths to the worker so
   * subsequent message fetches return only the requested paths. Invoked
   * when the user adds, removes, or reorders paths in the panel, or drops
   * a new topic onto an empty panel.
   *
   * Cache invalidation is split by which dimension of the panel state
   * changed:
   *
   * - Topic changed (or panel emptied): the timestamp index no longer
   *   describes the relevant data, so the entire cache is dropped and the
   *   last-emitted index — which seeds the playback direction inference
   *   in `loadMessages` — is reset. The worker will deliver a new index
   *   via a `TimeIndexStateChange` event (the "the time index was
   *   rebuilt" notification from the worker; see
   *   `isTimeIndexStateChangeEvent`) and `handleMessage` will install it.
   * - Path selection changed within the same topic: the timestamp index
   *   is still valid, so it is preserved. The cached message bodies are
   *   marked stale via `RawMessageCache.markCacheStale`, which clears the
   *   per-entry freshness sidecar. Existing entries remain readable so
   *   the panel keeps rendering the previous frame, but
   *   `getIndicesForMissingData` will report them as missing on the next
   *   poll, prompting a re-fetch with the new path projection.
   *
   * Assumes all entries share the same topic — enforced by
   * `canAddToRawMessagePanel` at the drag-and-drop level and by the
   * `AddDataToRawMessagePanel` reducer. Topic identity is read from the
   * first element only.
   */
  setState(topicData: TopicData[], signal?: AbortSignal): void {
    const nextTopicId = topicData[0]?.topic.id ?? null;
    const topicChanged = this.prevTopicId !== nextTopicId;
    this.prevTopicId = nextTopicId;

    // Topic changed or panel emptied: the existing timestamp index is no
    // longer relevant, so drop everything and let the worker rebuild.
    // Same-topic update: keep the timestamp index but mark message
    // bodies stale so the next poll re-fetches under the new paths.
    if (topicChanged || topicData.length === 0) {
      this.cache.clear();
      this.lastEmittedIndex = null;
    } else {
      this.cache.markCacheStale();
    }

    this.workerManager.sendCommand({
      data: topicData,
      source: SOURCE,
      type: RawMessageCommand.SetState,
      signal,
    });

    // On a same-topic path-only update the panel must reflect the new
    // path selection without waiting for a worker round-trip. The worker
    // only emits `TimeIndexStateChange` when the topic identity changes,
    // so on a path-only update no event will arrive to drive a refresh.
    // Re-emit the cached message at the playhead synchronously: the
    // entries are stale but still readable, so the panel updates
    // immediately, and the next poll cycle re-fetches the message bodies
    // under the new paths.
    if (!topicChanged && topicData.length > 0 && this.hasIndex) {
      this.emitMessageAtCurrentTime();
    }
  }

  /**
   * Bind the workspace timer so the panel stays aligned with the playhead.
   * Aborting `abortController` removes all three listeners in one shot,
   * which is how the panel detaches from a swapped-out timer.
   *
   * Each timer event only re-emits the message already cached at the new
   * playhead position; gap-filling is the `useMessages` poller's job (see
   * `loadMessages`). Splitting these responsibilities keeps timer ticks
   * cheap — every frame would otherwise risk launching a worker request.
   */
  setTimer(timer: Timer, abortController: AbortController): void {
    this.timer = timer;
    timer.addListener("tick", () => this.emitMessageAtCurrentTime(), {
      signal: abortController.signal,
    });
    timer.addListener("seek", () => this.emitMessageAtCurrentTime(), {
      signal: abortController.signal,
    });
    timer.addListener("start", () => this.emitMessageAtCurrentTime(), {
      signal: abortController.signal,
    });
  }

  private downloadState(): DownloadPriority | false {
    const states = Object.values(this.loadingStates);
    for (const state of ["foreground", "background"] as DownloadPriority[]) {
      if (states.includes(state)) {
        return state;
      }
    }
    return false;
  }

  /**
   * Push the cached message at the playhead to subscribers. Emits `null`
   * when the playhead is outside the topic's recorded range, and emits
   * nothing at all when the requested index is in range but the message
   * body has not yet been fetched — the next `loadMessages` poll cycle
   * will fill the gap and emit then.
   */
  private emitMessageAtCurrentTime(): void {
    if (!this.timer) {
      return;
    }
    const idx = this.cache.timestampToIndex(this.timer.currentTime);
    const msg = idx !== -1 ? this.cache.indexToMessage(idx) : null;
    if (msg !== undefined) {
      this.onMessageCallback?.(msg ?? null);
    }
  }

  private handleMessage = (event: MessageEvent<MessagePayload>): void => {
    if (isInitializedEvent(event)) {
      this.onInitializedCallback();
      return;
    }

    if (isTimeIndexStateChangeEvent(event)) {
      const { state, timestamps, sequences } = event.data.data;
      if (state === IndexState.Built && timestamps && sequences) {
        this.cache.setTimestampIndex(
          new BigUint64Array(timestamps),
          new Uint32Array(sequences),
        );
        // The cache was just cleared by `setTimestampIndex`, so the
        // only state this can emit is `null` — when the timer's
        // current time falls before the first indexed timestamp. That
        // is the case worth handling synchronously: it surfaces the
        // "no recorded messages" placeholder on the first drop into
        // an empty panel without waiting for the next poll cycle. For
        // an in-range playhead the next poll will fetch and emit.
        this.emitMessageAtCurrentTime();
      } else if (state === IndexState.Empty) {
        // The selected topic has no messages in the active range.
        // Clearing the loading flag is load-bearing even when no fetch
        // looks in flight: a poll cycle issued just before this event
        // may have set the indicator, and leaving it on would show a
        // spinner over the "no recorded messages" placeholder forever.
        this.cache.clear();
        this.onMessageCallback?.(null);
        this.loadingStateChange("UIThread", false);
      }
      return;
    }

    if (isLoadingStateChangeEvent(event)) {
      this.loadingStateChange("WebWorker", event.data.data.state);
      return;
    }

    if (isErrorEvent(event)) {
      if (event.data.error === undefined) {
        LoggerService.warn(
          "RawMessageManager: Error event without error detail",
        );
        return;
      }
      const error = new Error(event.data.error.message);
      error.name = event.data.error.name ?? "Error fetching raw message data";
      error.stack = event.data.error.stack;
      this.onErrorCallback?.(error);
      return;
    }
  };

  private loadingStateChange(realm: Realm, state: DownloadPriority | false) {
    const prev = this.downloadState();
    this.loadingStates[realm] = state;
    const next = this.downloadState();
    if (prev !== next) {
      this.onLoadingChangeCallback?.(next);
    }
  }
}
